import { deleteDoc, doc } from 'firebase/firestore';
import type { SupportDoc } from '../../components/TenderDetails/types';
import { deleteSupportDocForTender, deleteTenderForm } from '../../services/api';
import { firestore } from '../../services/firebase';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { logDocumentActivity } from '../../utils/activityLogger';

export interface UseSupportDocsDeletionProps {
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setUser_docs: React.Dispatch<React.SetStateAction<SupportDoc[]>>;
  tenderCVs: SupportDoc[];
}

export const useSupportDocsDeletion = ({
  tender,
  account,
  showSnackbar,
  setLoadingStates,
  setUser_docs,
  tenderCVs,
}: UseSupportDocsDeletionProps) => {
  const handleDeleteSupportDoc = async (supportDoc: SupportDoc) => {
    if (!account?.uid || !tender?.id || !supportDoc?.originalFilename || !account?.clientId) {
      showSnackbar('Missing critical information for deleting support document.', 'error');
      return;
    }
    const loadingKey = `delete_${supportDoc.originalFilename}`;
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const baseFileName = supportDoc.originalFilename.replace(/\.[^/.]+$/, '');
      const txtFileName = `${baseFileName}.txt`;

      await deleteSupportDocForTender({
        userId: account.uid,
        clientId: account.clientId,
        tenderId: tender.id,
        fileName: txtFileName,
        ...(supportDoc.id && { docId: supportDoc.id }),
      });

      if (supportDoc.originalFilename) {
        const matchingCV = tenderCVs.find(
          (cv) => cv.originalFilename === supportDoc.originalFilename
        );
        if (matchingCV) {
          const cvId = matchingCV.id || supportDoc.originalFilename;
          const cvRef = doc(firestore, 'tenders', tender.id, 'cvs', cvId);
          try {
            await deleteDoc(cvRef);
          } catch (_e) {
            // Ignore
          }
        }
      }

      showSnackbar(
        `Support document "${supportDoc.originalFilename}" deleted successfully.`,
        'success'
      );
      logDocumentActivity.deleted({
        type: 'support_document',
        fileName: supportDoc.originalFilename,
        source: `tender_details_dialog/${tender.id}`,
      });
      setUser_docs((prev) =>
        prev.filter((d) => d.originalFilename !== supportDoc.originalFilename)
      );
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      if (err.response?.status === 403 && err.response?.data?.message) {
        showSnackbar(`⚠️ LIMIT EXCEEDED: ${err.response.data.message}`, 'warning', 7000);
      } else {
        showSnackbar(
          `Failed to delete ${supportDoc.originalFilename}: ${err.message}`,
          'error',
          2000
        );
      }
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDeleteTenderForm = async (docObj: SupportDoc) => {
    const filename = docObj.originalFilename || docObj.name || docObj.filename || docObj.id;
    if (!account?.uid || !tender?.id || !filename || !account?.clientId) {
      showSnackbar('Missing critical information for deleting tender form.', 'error');
      return;
    }
    const loadingKey = `delete_form_${filename}`;
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const baseFileName = filename.replace(/\.[^/.]+$/, '');
      const txtFileName = `${baseFileName}.txt`;

      await deleteTenderForm({
        userId: account.uid,
        clientId: account.clientId,
        tenderId: tender.id,
        fileName: txtFileName,
        ...(docObj.id && { docId: docObj.id }),
      });
      showSnackbar(`Form "${filename}" deleted successfully.`, 'success');
      logDocumentActivity.deleted({
        type: 'tender_form',
        fileName: filename,
        source: `tender_details_dialog/${tender.id}`,
      });
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      showSnackbar(`Failed to delete ${filename}: ${err.message}`, 'error', 2000);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  return {
    handleDeleteSupportDoc,
    handleDeleteTenderForm,
  };
};
